// If you want to use Phoenix channels, run `mix help phx.gen.channel`
// to get started and then uncomment the line below.
// import "./post_socket.js";

// You can include dependencies in two ways.
//
// The simplest option is to put them in assets/vendor and
// import them using relative paths:
//
//     import "../vendor/some-package.js"
//
// Alternatively, you can `npm install some-package --prefix assets` and import
// them using a path starting with the package name:
//
//     import "some-package"
//

// Include phoenix_html to handle method=PUT/DELETE in forms and buttons.
import "phoenix_html";
// Establish Phoenix Socket and LiveView configuration.
import { Socket } from "phoenix";
import { LiveSocket } from "phoenix_live_view";
import { SupportHook, AuthenticationHook, RegistrationHook } from "./main.js";
import Uploaders from "./uploaders.mjs";
import { applyAppBadge } from "./app_badge.mjs";
import FlashyHooks from "flashy";
import PwaHook from "./pwa_hook.js";
import PwaInstallHook from "./pwa_install_hook.js";

import topbar from "../vendor/topbar";
import { OtpSheet } from "./OtpSheet.js";
import InfiniteScrollBottom from "./infinite_scroll_bottom.js";
import PullToRefresh from "./pull_to_refresh.js";
import TextareaFocus from "./textarea_focus.js";
import MessageSwipeManager from "./message_swipe_manager.js";
import NotificationsHook from "./notifications_hook.js";
import CaptchaHook from "./CaptchaHook.js";
import LikeAnimation from "./like_animation_hook.js";
import MentionHook from "./mention_hook.js";
import RepostAnimation from "./repost_animation_hook.js";
import ChartHook from "./chart_js.js";
import AudioRecorder from "./audio_recorder_hook.js";
import AudioPlayer from "./audio_player.js";
import SpinWheel from "./spin_wheel_hook.js";
import SpinWheelGame from "./spin_wheel_game_hook.js";
import DragWheelSections from "./drag_wheel_sections_hook.js";
import AnimateBalance from "./animate_balance_hook.js";
import TransferAnimation from "./transfer_animation_hook.js";
import WheelReplay from "./wheel_replay_hook.js";
import ScrollFade from "./scroll_fade_hook.js";
import SwipeContainer from "./swipe_container_hook.js";
import DoubleTap from "./double_tap_hook.js";
import ImagePrefetch from "./image_prefetch_hook.js";
import MessageReactions from "./message_reactions_hook.js";
import TributeRangeSlider from "./tribute_range_slider_hook.js";
import { Hooks as FluxonHooks, DOM as FluxonDOM } from "fluxon";
import JSConfetti from "js-confetti";

const jsConfetti = new JSConfetti();

window.addEventListener("phx:live_reload:attached", ({ detail: reloader }) => {
    reloader.enableServerLogs();
});

let csrfToken = document.querySelector("meta[name='csrf-token']").getAttribute("content");

const hooks = {
    AnimateBalance,
    AudioPlayer,
    AudioRecorder,
    AuthenticationHook,
    CaptchaHook,
    ChartHook,
    DoubleTap,
    DragWheelSections,
    ImagePrefetch,
    InfiniteScrollBottom,
    LikeAnimation,
    MentionHook,
    MessageReactions,
    MessageSwipeManager,
    NotificationsHook,
    OtpSheet,
    PullToRefresh,
    PwaHook,
    PwaInstallHook,
    RegistrationHook,
    RepostAnimation,
    ScrollFade,
    SpinWheel,
    SpinWheelGame,
    SupportHook,
    SwipeContainer,
    TextareaFocus,
    TransferAnimation,
    TributeRangeSlider,
    WheelReplay,
    ...FlashyHooks,
    ...FluxonHooks,
};

let liveSocket = new LiveSocket("/live", Socket, {
    params: {
        _csrf_token: csrfToken,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
    uploaders: Uploaders,
    hooks: hooks,
    dom: {
        onBeforeElUpdated(from, to) {
            FluxonDOM.onBeforeElUpdated(from, to);
        },
    },
});

// Show progress bar on live navigation and form submits
topbar.config({ barColors: { 0: "#29d" }, shadowColor: "rgba(0, 0, 0, .3)" });

window.addEventListener("phx:page-loading-start", (_info) => topbar.show(300));
window.addEventListener("phx:page-loading-stop", (_info) => topbar.hide());
window.addEventListener("phx:copy", (event) => {
    let text = event.detail.address; // Alternatively use an element or data tag!
    if (!navigator.clipboard) {
        console.error("Clipboard API unavailable (insecure context?); cannot copy", text);
        return;
    }
    navigator.clipboard.writeText(text).catch((err) => console.error("Clipboard write failed", err));
});

window.addEventListener("phx:navigate_back", () => {
    window.history.back();
});

window.addEventListener("phx:js-exec", ({ detail }) => {
    document.querySelectorAll(detail.to).forEach((el) => {
        liveSocket.execJS(el, el.getAttribute(detail.attr));
    });
});

window.addEventListener("phx:scroll_to_top", (e) => {
    let el = document.querySelector(e.detail.to);
    if (el) {
        el.scrollTo({ top: 0, behavior: "smooth" });
    }
});

window.addEventListener("phx:js-confetti", () => {
    jsConfetti.addConfetti({ emojis: ["💰", "💸", "🤑"] });
});

// Lives here rather than in NotificationsHook because that hook is only mounted on /home,
// and the app icon has to stay correct from whichever screen the user is on.
window.addEventListener("phx:set_app_badge", ({ detail }) => {
    applyAppBadge(navigator, detail.count);
});

customElements.define(
    "video-preview",
    class VideoPreview extends HTMLElement {
        connectedCallback() {
            const ref = this.getAttribute("ref");
            const name = this.getAttribute("name") || "videos";
            const input = document.querySelector(`input[type="file"][name="${name}"]`);
            const files = input.phxPrivate.files;
            const file = files.find((e) => e._phxRef == ref);
            const video = this.querySelector("video");
            video.setAttribute("playsinline", "");
            video.muted = true;
            video.src = URL.createObjectURL(file);

            video.onloadedmetadata = () => {
                video.currentTime = 0.001;
            };
            // const source = this.querySelector("source");
            // source.setAttribute("src", URL.createObjectURL(file));
        }
    },
);

customElements.define(
    "audio-preview",
    class AudioPreview extends HTMLElement {
        connectedCallback() {
            const ref = this.getAttribute("ref");
            const name = this.getAttribute("name") || "audio";
            const input = document.querySelector(`input[type="file"][name="${name}"]`);
            const files = input.phxPrivate.files;
            const file = files.find((e) => e._phxRef == ref);
            const audio = this.querySelector("audio");
            const source = this.querySelector("source");

            if (file && source && audio) {
                const url = URL.createObjectURL(file);
                source.setAttribute("src", url);
                audio.load();
            }
        }
    },
);

window.addEventListener("resize", forceViewportUpdate);

forceViewportUpdate();

function forceViewportUpdate() {
    // Small delay to ensure browser UI has finished animating
    setTimeout(() => {
        window.scrollTo(0, 0);
        // Force reflow
        document.body.style.minHeight = `${window.innerHeight}px`;
        requestAnimationFrame(() => {
            document.body.style.minHeight = "";
        });
    }, 100);
}
// connect if there are any LiveViews on the page
liveSocket.connect();

// expose liveSocket on window for web console debug logs and latency simulation:
// liveSocket.enableDebug();
// liveSocket.enableProfiling();
liveSocket.disableDebug();
liveSocket.disableLatencySim();
liveSocket.disableProfiling();
// >> liveSocket.enableLatencySim(1000)  // enabled for duration of browser session
// liveSocket.disableLatencySim();
window.liveSocket = liveSocket;
